import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { Typography, Box, IconButton, Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress, Button } from '@mui/material';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Badge from 'react-bootstrap/Badge';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { formatDate, safeFormatDateOnly } from 'src/utils/DateUtils';

const RESCHEDULE_COLUMNS = [
  {
    key: 'orderNo',
    header: 'Order No.',
    sortable: true,
    width: '140px',
    render: (val) => <span style={{ fontWeight: 700, color: '#1e293b' }}>{val || '—'}</span>
  },
  {
    key: 'erpOrderNo',
    header: 'ERP Order No.',
    sortable: true,
    width: '140px',
    render: (val, row) => <span style={{ fontWeight: 600, color: '#475569' }}>{val || row.erpNo || '—'}</span>
  },
  {
    key: 'requestedDateTimeSlot',
    header: 'Req. Date',
    sortable: true,
    width: '135px',
    render: (value) => value ? safeFormatDateOnly(value) : '—',
  },
  {
    key: 'requestedTimeSlot',
    header: 'Time Slot',
    sortable: true,
    width: '120px',
  },
  {
    key: 'areaName',
    header: 'Area',
    sortable: true,
    width: '140px',
  },
  {
    key: 'orderStatusInfo.orderStatusDes',
    header: 'Status',
    sortable: true,
    width: '120px',
    render: (val, row) => (
      <span style={{ fontWeight: 600, color: '#475569' }}>
        {val || row.orderStatusInfo?.orderStatusDes || row.orderStatusDes || row.orderStatus || '—'}
      </span>
    )
  },
  {
    key: 'orderType',
    header: 'Order Type',
    sortable: true,
    width: '90px',
    render: (val) => (
      <>
        {val === 1 ? (
          <Badge bg="success" className="px-2 py-1">DO</Badge>
        ) : val === 2 ? (
          <Badge bg="primary" className="px-2 py-1">PU</Badge>
        ) : (
          <Badge bg="warning" className="px-2 py-1">PnD</Badge>
        )}
      </>
    )
  },
];

const OrderDetailsView = ({ reactTableData, onReschedule, onOpenMap }) => {
  const rows = reactTableData?.tableRowsData || [];
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Auto-select first row when list loads
  useEffect(() => {
    if (rows.length > 0) {
      setSelectedOrder(rows[0]);
    } else {
      setSelectedOrder(null);
    }
  }, [reactTableData]);

  // Fetch box items when selected order changes
  useEffect(() => {
    if (selectedOrder?.orderId) {
      fetchOrderItems(selectedOrder.orderId);
    } else {
      setOrderItems([]);
    }
  }, [selectedOrder]);

  const fetchOrderItems = async (orderId) => {
    setItemsLoading(true);
    try {
      const response = await PndRestApi.GetOrderItemWiseDetails(orderId);
      if (response.status === 200 && response.data.status === 200) {
        setOrderItems(response?.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
    } finally {
      setItemsLoading(false);
    }
  };

  return (
    <Row>
      {/* Left Pane: Table */}
      <Col md={8} lg={8}>
        <RadioSelectTable
          rows={rows}
          columns={RESCHEDULE_COLUMNS}
          rowKey="orderId"
          selectedKey={selectedOrder?.orderId}
          onSelect={setSelectedOrder}
          searchKeys={['orderNo', 'salesOrderNo', 'erpNo', 'areaName', 'orderStatusInfo.orderStatusDes']}
          searchPlaceholder="Search by Order No, ERP No..."
          sortKey="orderNo"
          headerTitle="Rescheduled Orders"
          pageSizes={[5, 10, 20, 50]}
          minWidth="750px"
        />
      </Col>

      {/* Right Pane: Detail Panel */}
      <Col md={4} lg={4}>
        <Card style={{
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
          minHeight: '520px',
          background: '#ffffff',
          overflow: 'hidden',
        }}>
          {selectedOrder ? (
            <>
              {/* Header Banner */}
              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                padding: '24px 20px',
                color: '#fff',
                position: 'relative'
              }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', opacity: 0.8, fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                  Order Reschedule Details
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {selectedOrder.orderNo}
                </Typography>
                {selectedOrder.salesOrderNo && (
                  <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                    ERP No: {selectedOrder.erpOrderNo}
                  </Typography>
                )}

                {/* Header Action Buttons */}
                <Box sx={{ position: 'absolute', top: 20, right: 16, display: 'flex', gap: 1 }}>
                  {onOpenMap && selectedOrder.latitude && selectedOrder.longitude && (
                    <Tooltip title="View Delivery Area on Map" arrow>
                      <IconButton
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' } }}
                        onClick={() => onOpenMap(selectedOrder)}
                      >
                        <LocationOnIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Reschedule Order Now" arrow>
                    <IconButton
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' } }}
                      onClick={() => onReschedule(selectedOrder)}
                    >
                      <AssignmentIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </div>

              <CardBody style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Section: Client & Location */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#8b5cf6' }}>
                    <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Client & Delivery Area</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {selectedOrder.name || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.82rem', lineHeight: 1.5 }}>
                    {selectedOrder.address || 'N/A'}
                  </Typography>
                  {selectedOrder.contactNo && (
                    <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, display: 'block', mt: 0.5 }}>
                      Contact: {selectedOrder.contactNo}
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* Section: Timestamps */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#d97706' }}>
                    <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Reschedule Timestamps</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.82rem' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'between' }}>
                      <span style={{ color: '#64748b' }}>Requested Date:</span>
                      <span style={{ fontWeight: 600, color: '#1e293b', marginLeft: 'auto' }}>
                        {formatDate(selectedOrder.requestedDateTimeSlot)}
                      </span>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'between' }}>
                      <span style={{ color: '#64748b' }}>Scheduled Date:</span>
                      <span style={{ fontWeight: 600, color: '#1e293b', marginLeft: 'auto' }}>
                        {formatDate(selectedOrder.scheduledDateTimeSlot)}
                      </span>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Section: Financials */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#059669' }}>
                    <PaymentIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Financial Summary</Typography>
                  </Box>
                  <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: '8px', border: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'between', mb: 1 }}>
                      <span style={{ color: '#64748b' }}>Method:</span>
                      <span style={{ fontWeight: 600, color: '#1e293b', marginLeft: 'auto' }}>
                        {selectedOrder.paymentMethodInfo?.description || 'N/A'}
                      </span>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'between', fontWeight: 'bold' }}>
                      <span style={{ color: '#475569' }}>Rider Collection Amount:</span>
                      <span style={{ color: '#2563eb', marginLeft: 'auto' }}>
                        {selectedOrder.riderCollectionAmount || '0.00'}
                      </span>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Section: Box Barcodes (Inline table) */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#0284c7' }}>
                    Box Item Details
                  </Typography>
                  {itemsLoading ? (
                    <LinearProgress sx={{ my: 2 }} />
                  ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                          <TableRow>
                            <TableCell sx={{ py: 1, fontWeight: 'bold', fontSize: '0.75rem' }}>Barcode</TableCell>
                            <TableCell sx={{ py: 1, fontWeight: 'bold', fontSize: '0.75rem' }}>Weight/Qty</TableCell>
                            <TableCell sx={{ py: 1, fontWeight: 'bold', fontSize: '0.75rem', textAlign: 'center' }}>In/Out</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderItems.map((item, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600, color: '#2563eb' }}>
                                {item.boxBarCode}
                              </TableCell>
                              <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                {item.weightKg ? `${item.weightKg} Kg` : '--'} / {item.noOfItems || 1}
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                  {item.isScannedInn ? <CheckCircleIcon color="success" sx={{ fontSize: 14 }} /> : <CancelIcon color="error" sx={{ fontSize: 14 }} />}
                                  {item.isScannedOut ? <CheckCircleIcon color="success" sx={{ fontSize: 14 }} /> : <CancelIcon color="error" sx={{ fontSize: 14 }} />}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                          {orderItems.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ py: 2, textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
                                No barcode items found.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>

                {/* Warning Alert if orderRatsHistoryInfo is null */}
                {selectedOrder.orderRatsHistoryInfo === null && (
                  <Box sx={{ bgcolor: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600 }}>
                      Rescheduling is currently unavailable. The assigned rider is still processing other orders in the same trip. Please wait until the current trip is completed to reschedule this order. This ensures the rider's order history is preserved.
                    </Typography>
                  </Box>
                )}

                {/* Footer Action */}
                <Box sx={{ mt: 'auto', pt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    startIcon={<ScheduleIcon />}
                    disabled={selectedOrder.orderRatsHistoryInfo === null}
                    onClick={() => onReschedule(selectedOrder)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      padding: '8px',
                      background: selectedOrder.orderRatsHistoryInfo === null
                        ? '#cbd5e1'
                        : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                      boxShadow: 'none',
                      fontWeight: 'bold',
                      '&:hover': {
                        boxShadow: 'none',
                        background: selectedOrder.orderRatsHistoryInfo === null
                          ? '#cbd5e1'
                          : 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
                      }
                    }}
                  >
                    RESCHEDULE ORDER

                  </Button>
                </Box>
              </CardBody>
            </>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '40px',
              color: '#94a3b8',
              textAlign: 'center',
              minHeight: '520px'
            }}>
              <InfoIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body1">Select an order from the list to view reschedule details.</Typography>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
};

OrderDetailsView.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  }).isRequired,
  onReschedule: PropTypes.func.isRequired,
  onOpenMap: PropTypes.func
};

export default OrderDetailsView;